<template>
  <div class="mx-10 ma-5">
    <div>
      <v-card
        class="pa-5"
        :style="{ border: '1px solid var(--primary-boarder-color)' }"
      >
        <PageTitleWithSearch
          :title="'Customers'"
          :btnName="'Add New Customer'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/customers/create'"
          :btnPermission="'customer.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        >
        </PageTitleWithSearch>
        <v-data-table
          v-if="!loading && customers"
          :headers="headers"
          :items="customers"
          :items-per-page="10"
          :hide-default-footer="true"
          :disable-sort="true"
          :disable-pagination="true"
          id="data-table"
        >
          <template v-slot:item="{ item, index }">
            <tr
              :class="index % 2 === 0 ? 'dark-row' : 'light-row'"
              class="custom-row"
            >
              <td>
                <v-avatar size="36px">
                  <img
                    v-if="item.profile_image"
                    :src="item.profile_image"
                    style="width: 100%; height: 100%;"
                  />
                  <img
                    v-else
                    :src="defaultImage"
                    style="width: 100%; height: 100%;"
                  />
                </v-avatar>
              </td>
              <td class="text-left">{{ item.code }}</td>
              <td class="text-left">{{ item.company_name }}</td>
              <td class="text-left">
                <router-link
                  :to="'/customer/' + item.uuid + '/info'"
                  class="router-link font-weight-medium"
                  >{{ item.title }} {{ item.full_name }}</router-link
                >
              </td>
              <td class="text-left">{{ item.email }}</td>
              <td class="text-left">{{ item.mobile }}</td>
              <td class="text-left">
                {{ item.city }}, {{ item.state }}, {{ item.country }}
              </td>
              <td class="text-center" v-if="item.status">
                <v-chip
                  small
                  class="ma-2"
                  dark
                      :color="getStatusColor(item.status).chip"
                  :text-color="getStatusColor(item.status).text"
                  >{{ stringFormatting(item.status) }}</v-chip
                >
              </td>
              <td class="text-center">
                <v-btn
                  v-if="hasPermission('customer.edit')"
                  color="#FFE0B2"
                  :to="'/customers/edit/' + item.uuid"
                  fab
                  small
                  dark
                  style="box-shadow: none;"
                  class="mx-2"
                >
                  <v-icon small color="#FF9800"> mdi-pencil </v-icon>
                </v-btn>
                <v-btn
                  v-if="hasPermission('customer.delete')"
                  small
                  color="#FFCDD2"
                  @click="dropMemberConfirm(item.uuid)"
                  fab
                  style="box-shadow: none;"
                  class="mx-2"
                >
                  <v-icon dark small color="#F44336">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-row class="my-1">
        <Pagination
          v-model="page"
          :loading="loading"
          :total="total"
          :page="page"
        ></Pagination>
      </v-row>
      </v-card>
    </div>

    <ConfirmModal
      :text="'Do you want to drop this customer?'"
      :title="'Drop Customer'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Pagination from "@/components/shared/Pagination";
import Snackbars from "@/components/shared/Snackbars";
import { CustomerService } from "@/services/customer-service.js";
import { checkError } from "@/config/errors.js";
import { getActiveStatusColors } from "@/utility/statusColors.js";

export default {
  name: "CustomerGrid",
  components: { PageTitleWithSearch, Pagination, ConfirmModal, Snackbars },
  data: function() {
    return {
      customers: [],
      page: 1,
      search: "",
      loading: false,
      role: localStorage.getItem("role") || null,
      total: 0,
      totalCustomer: 0,
      dropId: null,
      confirmOpen: false,
      snackbar: false,
      successMsg: "This customer has ongoing projects. Can not delete",
      defaultImage: require("@/assets/img/avatar.svg"),
      headers: [
        { text: "", value: "customer_profile", align: "center" },
        { text: "Customer Code", value: "customer_code", align: "start" },
        { text: "Company Name", value: "company_name", align: "start" },
        { text: "Contact Person", value: "name" },
        { text: "Email", value: "email", align: "start" },
        { text: "Mobile", value: "mobile", align: "start" },
        { text: "Location", value: "city" },
        { text: "Status", value: "status", align: "center" },
        { text: "Actions", value: "actions", align: "center" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      profile: "profile",
      hasPermission: "hasPermission",
    }),
  },
  mounted() {
    this.$store.dispatch("auth/getProfile");
    this.getCustomer();
  },

  watch: {
    search() {
      this.page = 1;
      this.getCustomer();
    },
    page() {
      this.getCustomer();
    },
  },

  methods: {
    getCustomer() {
      this.loading = true;
      CustomerService.getCustomerList(this.search, this.page)
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.customers = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalCustomer = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.customers = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },
    stringFormatting(str) {
      return str.replace(/_/g, " ");
    },
    getStatusColor(status) {
      return getActiveStatusColors(status);
    },

    searchChange(value) {
      this.search = value;
    },

    canCreateEdit() {
      if (
        this.role === "ADMINISTRATOR" ||
        this.role === "MANAGER" ||
        this.role === "USER"
      ) {
        return true;
      } else {
        return false;
      }
    },

    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },

    dropMember() {
      if (this.dropId) {
        this.loading = true;
        CustomerService.dropMember(this.dropId)
          .then((response) => {
            if (response.data == "Success") {
              this.loading = false;
              this.getCustomer();
            } else {
              this.loading = false;
              this.snackbar = true;
            }
          })
          .catch((error) => {
            this.loading = false;
            this.snackbar = false;
            let data = error.response.data;
            let errorOutput = checkError.errorFormatting(data);
            this.errors = errorOutput;
          });
      }
    },

    canDrop() {
      if (this.role === "ADMINISTRATOR") {
        return true;
      } else {
        return false;
      }
    },

    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
